import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useOrganization } from '../../context/OrganizationContext';
import { fetchWithOrg } from '../../utils/api';

import {
  ArrowsClockwise,
  Calendar,
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Clock,
  DownloadSimple,
  Eye,
  FileText,
  Lightning,
  ListChecks,
  DotsThree,
  Pause,
  Play,
  Pulse,
  Warning,
  XCircle
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../../config';

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    running: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CircleNotch, spin: true },
    queued: { color: 'bg-yellow-500/20 text-[#F59E0B] border-yellow-500/30', icon: Clock },
    waiting_for_approval: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Warning },
    blocked: { color: 'bg-red-500/20 text-[#FF6B6B] border-red-500/30', icon: XCircle },
    succeeded: { color: 'bg-green-500/20 text-[#00E5C0] border-green-500/30', icon: CheckCircle },
    failed: { color: 'bg-red-500/20 text-[#FF6B6B] border-red-500/30', icon: XCircle },
    canceled: { color: 'bg-gray-500/20 text-[#B0B8C4] border-gray-500/30', icon: XCircle },
    completed: { color: 'bg-green-500/20 text-[#00E5C0] border-green-500/30', icon: CheckCircle },
  };

  const config = statusConfig[status] || statusConfig.queued;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon size={12} className={config.spin ? 'animate-spin' : ''} />
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

// Task row component
const TaskRow = ({ task, isExpanded, onToggle, onApprove, onCancel }) => {
  const [showEvents, setShowEvents] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);

  const loadEvents = async () => {
    if (!task.id) return;
    setLoadingEvents(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/${task.agent_id}/tasks/${task.id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to load task events:', error);
    }
    setLoadingEvents(false);
  };

  const loadArtifacts = async () => {
    if (!task.id) return;
    setLoadingArtifacts(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/workspace/artifacts/${task.id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setArtifacts(data.artifacts || []);
      }
    } catch (error) {
      console.error('Failed to load artifacts:', error);
    }
    setLoadingArtifacts(false);
  };

  const downloadArtifact = async (artifact) => {
    try {
      window.open(`${BACKEND_URL}/api/exec/workspace/download/${artifact.id}`, '_blank');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  useEffect(() => {
    if (showEvents && events.length === 0) {
      loadEvents();
      loadArtifacts();
    }
  }, [showEvents]);

  return (
    <div className="border border-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden mb-2">
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
        onClick={() => setShowEvents(!showEvents)}
      >
        <button className="text-[#A0AEC0] hover:text-white">
          {showEvents ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white truncate">{task.title}</span>
            {task.progress_pct !== null && task.progress_pct !== undefined && (
              <span className="text-xs text-[#A0AEC0]">{task.progress_pct}%</span>
            )}
          </div>
          {task.description && (
            <p className="text-xs text-[#A0AEC0] truncate mt-0.5">{task.description}</p>
          )}
        </div>
        
        <StatusBadge status={task.status} />
        
        {task.status === 'waiting_for_approval' && (
          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onApprove(task.id); }}
              className="px-2 py-1 bg-green-500/20 text-[#00E5C0] rounded text-xs hover:bg-green-500/30 transition-colors"
            >
              Approve
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(task.id); }}
              className="px-2 py-1 bg-red-500/20 text-[#FF6B6B] rounded text-xs hover:bg-red-500/30 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
        
        {(task.status === 'queued' || task.status === 'blocked') && (
          <button
            onClick={(e) => { e.stopPropagation(); onCancel(task.id); }}
            className="px-2 py-1 bg-gray-500/20 text-[#B0B8C4] rounded text-xs hover:bg-gray-500/30 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
      
      {/* Expanded Events View */}
      <AnimatePresence>
        {showEvents && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-[rgba(255,255,255,0.1)] bg-[rgba(0,0,0,0.2)]"
          >
            <div className="p-3 space-y-2">
              {task.error_message && (
                <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-[#FF6B6B]">
                  <strong>Error:</strong> {task.error_message}
                </div>
              )}
              
              {task.approval_reason && (
                <div className="p-2 bg-orange-500/10 border border-orange-500/20 rounded text-xs text-orange-400">
                  <strong>Approval needed:</strong> {task.approval_reason}
                </div>
              )}
              
              {loadingEvents ? (
                <div className="flex items-center justify-center py-4">
                  <CircleNotch size={16} className="animate-spin text-[#A0AEC0]" />
                </div>
              ) : events.length > 0 ? (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {events.map((event, idx) => (
                    <div key={event.id || idx} className="flex items-start gap-2 text-xs">
                      <span className="text-[#A0AEC0] whitespace-nowrap">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`
                        ${event.event_type === 'error' ? 'text-[#FF6B6B]' : ''}
                        ${event.event_type === 'status_changed' ? 'text-blue-400' : ''}
                        ${event.event_type === 'progress' ? 'text-[#00E5C0]' : ''}
                        ${event.event_type === 'recovery_started' ? 'text-orange-400' : ''}
                        ${event.event_type === 'file_written' ? 'text-[#00E5C0]' : ''}
                        ${event.event_type === 'workspace' ? 'text-cyan-400' : ''}
                        ${!['error', 'status_changed', 'progress', 'recovery_started', 'file_written', 'workspace'].includes(event.event_type) ? 'text-[#E2E8F0]' : ''}
                      `}>
                        {event.message}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#A0AEC0] py-2">No events recorded</p>
              )}
              
              {/* File Artifacts - P3 Downloads */}
              {artifacts.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.1)]">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={14} className="text-[#00E5C0]" />
                    <span className="text-xs font-medium text-[#00E5C0]">Generated Files</span>
                  </div>
                  <div className="space-y-1">
                    {artifacts.map((artifact) => (
                      <div 
                        key={artifact.id}
                        className="flex items-center justify-between p-2 bg-[rgba(255,255,255,0.02)] rounded text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <FileText size={12} className="text-[#A0AEC0]" />
                          <span className="text-white">{artifact.filename}</span>
                          {artifact.size && (
                            <span className="text-[#A0AEC0]">
                              ({artifact.size > 1024 
                                ? `${(artifact.size / 1024).toFixed(1)} KB` 
                                : `${artifact.size} B`})
                            </span>
                          )}
                        </div>
                        {artifact.download_available ? (
                          <button
                            onClick={() => downloadArtifact(artifact)}
                            className="flex items-center gap-1 px-2 py-1 rounded bg-[#00E5C0]/20 text-[#00E5C0] hover:bg-[#00E5C0]/30 transition-colors"
                            data-testid={`download-artifact-${artifact.id}`}
                          >
                            <DownloadSimple size={12} />
                            DownloadSimple
                          </button>
                        ) : (
                          <span className="text-[#7D8896] text-xs">File expired</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Progress bar component
const ProgressBar = ({ progress }) => (
  <div className="w-full h-1.5 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
    <motion.div
      className="h-full bg-gradient-to-r from-[#00E5C0] to-purple-500"
      initial={{ width: 0 }}
      animate={{ width: `${progress || 0}%` }}
      transition={{ duration: 0.3 }}
    />
  </div>
);

// Main TasksTab component
export const TasksTab = ({ operatorId, operator }) => {
  const { activeOrganization } = useOrganization();
  const [state, setState] = useState({
    current_run: null,
    current_task: null,
    queued_tasks: [],
    recent_completed: [],
    scheduled: [],
    poll_interval_ms: 10000,
    is_active: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);

  const fetchTaskState = useCallback(async () => {
    if (!activeOrganization) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operatorId}/tasks/state`,
        {},
        activeOrganization.organization_id
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch task state');
      }
      
      const data = await response.json();
      setState(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch task state:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [operatorId, activeOrganization]);

  // Initial fetch and polling setup
  useEffect(() => {
    fetchTaskState();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchTaskState]);

  // Adjust polling interval based on activity
  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    const interval = state.is_active ? 2000 : 10000;
    pollIntervalRef.current = setInterval(fetchTaskState, interval);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [state.is_active, fetchTaskState]);

  const handleApprove = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/${operatorId}/tasks/${taskId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Task approved');
        fetchTaskState();
      } else {
        toast.error('Failed to approve task');
      }
    } catch (err) {
      toast.error('Failed to approve task');
    }
  };

  const handleCancel = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/${operatorId}/tasks/${taskId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Task cancelled');
        fetchTaskState();
      } else {
        toast.error('Failed to cancel task');
      }
    } catch (err) {
      toast.error('Failed to cancel task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotch size={32} className="animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <ListChecks size={24} className="text-pink-500" />
          Task Queue
        </h2>
        <button
          onClick={fetchTaskState}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg glass text-[#A0AEC0] hover:text-white transition-colors"
        >
          <ArrowsClockwise size={14} />
          ArrowsClockwise
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-[#FF6B6B]">
          {error}
        </div>
      )}

      {/* Three-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* NOW Column */}
        <div className="glass rounded-xl p-4">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Pulse size={18} className="text-blue-400" />
            Now
          </h3>
          
          {state.current_run ? (
            <div className="space-y-4">
              {/* Current Run */}
              <div className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.1)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">Current Run</span>
                  <StatusBadge status={state.current_run.status} />
                </div>
                <p className="text-xs text-[#A0AEC0] truncate mb-2">
                  {state.current_run.user_message || 'Processing...'}
                </p>
                <div className="text-xs text-[#A0AEC0]">
                  Started: {new Date(state.current_run.started_at).toLocaleTimeString()}
                </div>
              </div>
              
              {/* Current Task */}
              {state.current_task && (
                <div className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg border border-blue-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white truncate flex-1">
                      {state.current_task.title}
                    </span>
                    <StatusBadge status={state.current_task.status} />
                  </div>
                  
                  {state.current_task.progress_pct !== null && (
                    <div className="mt-2">
                      <ProgressBar progress={state.current_task.progress_pct} />
                      <span className="text-xs text-[#A0AEC0] mt-1 block">
                        {state.current_task.progress_pct}% complete
                      </span>
                    </div>
                  )}
                  
                  {state.current_task.status === 'waiting_for_approval' && (
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handleApprove(state.current_task.id)}
                        className="flex-1 px-3 py-1.5 bg-green-500/20 text-[#00E5C0] rounded text-xs hover:bg-green-500/30 transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleCancel(state.current_task.id)}
                        className="flex-1 px-3 py-1.5 bg-red-500/20 text-[#FF6B6B] rounded text-xs hover:bg-red-500/30 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-[#A0AEC0]">
              <Lightning size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No active run</p>
              <p className="text-xs mt-1">Tasks will appear here when running</p>
            </div>
          )}
        </div>

        {/* QUEUE Column */}
        <div className="glass rounded-xl p-4">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Clock size={18} className="text-[#F59E0B]" />
            Queue
            {state.queued_tasks.length > 0 && (
              <span className="ml-auto text-xs bg-yellow-500/20 text-[#F59E0B] px-2 py-0.5 rounded-full">
                {state.queued_tasks.length}
              </span>
            )}
          </h3>
          
          {state.queued_tasks.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {state.queued_tasks.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onApprove={handleApprove}
                  onCancel={handleCancel}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#A0AEC0]">
              <Clock size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Queue empty</p>
              <p className="text-xs mt-1">Pending tasks will appear here</p>
            </div>
          )}
          
          {/* Recent Completed */}
          {state.recent_completed.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[rgba(255,255,255,0.1)]">
              <h4 className="text-sm font-medium text-[#A0AEC0] mb-3">Recently Completed</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {state.recent_completed.slice(0, 5).map(task => (
                  <div 
                    key={task.id}
                    className="flex items-center gap-2 p-2 rounded bg-[rgba(255,255,255,0.02)]"
                  >
                    {task.status === 'succeeded' ? (
                      <CheckCircle size={14} className="text-[#00E5C0]" />
                    ) : (
                      <XCircle size={14} className="text-[#FF6B6B]" />
                    )}
                    <span className="text-xs text-[#E2E8F0] truncate flex-1">{task.title}</span>
                    <span className="text-xs text-[#A0AEC0]">
                      {task.completed_at ? new Date(task.completed_at).toLocaleTimeString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* SCHEDULED Column */}
        <div className="glass rounded-xl p-4">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-[#00E5C0]" />
            Scheduled
          </h3>
          
          {state.scheduled.length > 0 ? (
            <div className="space-y-2">
              {state.scheduled.map(schedule => (
                <div 
                  key={schedule.id}
                  className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.1)]"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white">{schedule.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      schedule.enabled 
                        ? 'bg-green-500/20 text-[#00E5C0]' 
                        : 'bg-gray-500/20 text-[#B0B8C4]'
                    }`}>
                      {schedule.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {schedule.next_run_at && (
                    <p className="text-xs text-[#A0AEC0]">
                      Next: {new Date(schedule.next_run_at).toLocaleString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#A0AEC0]">
              <Calendar size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No scheduled tasks</p>
              <p className="text-xs mt-1">Proactive tasks will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TasksTab;
